import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    X,
    Phone,
    MessageSquare,
    Mail,
    Home,
    Zap,
    Check,
    Pencil,
    ChevronDown,
    Trash2,
    Archive,
    ArchiveRestore,
    Loader2,
} from 'lucide-react';
import { useUserProperties } from '../hooks/useUserProperties';
import TagsInput from '../../ui/TagsInput';
import { deriveAvailableTags } from '../../../utils/tags';
import { useAuth } from '../../../context/AuthContext';
import { getOrCreateConversation } from '../../../firebase/conversation.service';

/**
 * Mobile lead drawer displaying detailed lead information as a bottom sheet.
 * Fully functional: status, contact details, notes - all editable with persistence.
 */
export const MobileLeadDrawer = ({
    lead,
    onClose,
    onUpdateStatus,
    onUpdateDetails,
    onUpdateTags,
    onArchiveLead,
    onUnarchiveLead,
    onDeleteLead,
    leads = [],
    VALID_STATUSES = ['Active', 'Closed', 'Archived', 'Cold'],
}) => {
    const navigate = useNavigate();
    const { user } = useAuth();

    // Derive global tag suggestions from all leads
    const availableTags = useMemo(() => deriveAvailableTags(leads), [leads]);

    // Fetch user properties for property selection
    const { properties, loading: propertiesLoading } = useUserProperties();

    // Contact details editing state
    const [isEditingContact, setIsEditingContact] = useState(false);
    const [editedContact, setEditedContact] = useState({});
    const [isSavingContact, setIsSavingContact] = useState(false);

    // Notes editing state
    const [isEditingNotes, setIsEditingNotes] = useState(false);
    const [editedNotes, setEditedNotes] = useState('');
    const [isSavingNotes, setIsSavingNotes] = useState(false);

    // Interested properties editing state (supports multiple)
    const [isEditingProperty, setIsEditingProperty] = useState(false);
    const [editedPropertyIds, setEditedPropertyIds] = useState([]);
    const [isSavingProperty, setIsSavingProperty] = useState(false);

    // Status saving state
    const [isSavingStatus, setIsSavingStatus] = useState(false);

    // Tags saving state
    const [isSavingTags, setIsSavingTags] = useState(false);

    // Delete confirmation state
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Navigation-to-inbox loading state
    const [navigatingToInbox, setNavigatingToInbox] = useState(null);

    // Reset state when lead changes
    useEffect(() => {
        if (lead) {
            setEditedContact({
                firstName: lead.firstName || '',
                lastName: lead.lastName || '',
                email: lead.email || '',
                phone: lead.phone || '',
                address: lead.address || '',
            });
            setEditedNotes(lead.notes || '');
            // Support multiple interested properties
            const propIds = (lead.interestedProperties || []).map(p => p.id);
            setEditedPropertyIds(propIds);
        }
    }, [lead]);

    if (!lead) return null;

    // Status change handler
    const handleStatusChange = async (e) => {
        const newStatus = e.target.value;
        if (newStatus === lead.status) return;

        setIsSavingStatus(true);
        try {
            if (newStatus === 'Archived') {
                await onArchiveLead?.(lead.id);
            } else {
                if (lead.archived) {
                    await onUnarchiveLead?.(lead.id);
                }
                // Always update status unless we just unarchived to Active
                // (onUnarchiveLead already sets status to Active)
                const skipStatusUpdate = lead.archived && newStatus === 'Active';
                if (!skipStatusUpdate) {
                    await onUpdateStatus?.(lead.id, newStatus);
                }
            }
        } catch (error) {
            console.error('Error updating status:', error);
        } finally {
            setIsSavingStatus(false);
        }
    };

    // Tags handler
    const handleTagsChange = async (newTags) => {
        setIsSavingTags(true);
        try {
            await onUpdateTags?.(lead.id, newTags);
        } catch (error) {
            console.error('Error updating tags:', error);
        } finally {
            setIsSavingTags(false);
        }
    };

    // Contact details handlers
    const handleSaveContact = async () => {
        setIsSavingContact(true);
        try {
            await onUpdateDetails?.(lead.id, editedContact);
            setIsEditingContact(false);
        } catch (error) {
            console.error('Error updating contact:', error);
        } finally {
            setIsSavingContact(false);
        }
    };

    const handleCancelEditContact = () => {
        setEditedContact({
            firstName: lead.firstName || '',
            lastName: lead.lastName || '',
            email: lead.email || '',
            phone: lead.phone || '',
            address: lead.address || '',
        });
        setIsEditingContact(false);
    };

    // Notes handlers
    const handleSaveNotes = async () => {
        setIsSavingNotes(true);
        try {
            await onUpdateDetails?.(lead.id, { notes: editedNotes });
            setIsEditingNotes(false);
        } catch (error) {
            console.error('Error updating notes:', error);
        } finally {
            setIsSavingNotes(false);
        }
    };

    const handleCancelEditNotes = () => {
        setEditedNotes(lead.notes || '');
        setIsEditingNotes(false);
    };

    // Interested properties handlers (supports multiple)
    const handleToggleProperty = (propertyId) => {
        setEditedPropertyIds(prev => {
            if (prev.includes(propertyId)) {
                return prev.filter(id => id !== propertyId);
            }
            return [...prev, propertyId];
        });
    };

    const handleSaveProperty = async () => {
        setIsSavingProperty(true);
        try {
            // Build array of {id, address} for selected properties
            const interestedProperties = editedPropertyIds
                .map(id => {
                    const prop = properties.find(p => p.id === id);
                    return prop ? { id: prop.id, address: prop.address || prop.displayName } : null;
                })
                .filter(Boolean);

            await onUpdateDetails?.(lead.id, { interestedProperties });
            setIsEditingProperty(false);
        } catch (error) {
            console.error('Error updating interested properties:', error);
        } finally {
            setIsSavingProperty(false);
        }
    };

    const handleCancelEditProperty = () => {
        const propIds = (lead.interestedProperties || []).map(p => p.id);
        setEditedPropertyIds(propIds);
        setIsEditingProperty(false);
    };

    // Navigate to Inbox and open conversation for this lead
    const handleOpenConversation = async (channelHint) => {
        if (!user?.uid || !lead?.id) return;
        setNavigatingToInbox(channelHint);
        try {
            const conversation = await getOrCreateConversation(user.uid, lead);
            navigate('/inbox', {
                state: {
                    openConversationId: conversation.id,
                    channelHint,
                },
            });
        } catch (error) {
            console.error('Error opening conversation:', error);
        } finally {
            setNavigatingToInbox(null);
        }
    };

    // Archive handlers
    const handleArchive = async () => {
        try {
            if (lead.archived) {
                await onUnarchiveLead?.(lead.id);
            } else {
                await onArchiveLead?.(lead.id);
            }
        } catch (error) {
            console.error('Error archiving lead:', error);
        }
    };

    // Delete handlers
    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await onDeleteLead?.(lead.id);
            onClose();
        } catch (error) {
            console.error('Error deleting lead:', error);
        } finally {
            setIsDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    return (
        <div className="fixed inset-x-0 bottom-0 bg-white rounded-t-2xl shadow-sheet z-50 flex flex-col h-[85vh] slide-up">
            {/* Drag handle */}
            <div className="w-full flex justify-center pt-3 pb-1" onClick={onClose}>
                <div className="w-12 h-1.5 bg-stone-300 rounded-full"></div>
            </div>

            {/* Header */}
            <div className="px-5 pb-4 border-b border-notion-border flex-shrink-0">
                <div className="flex justify-between items-start mb-4 mt-2">
                    <div className="flex-1">
                        {isEditingContact ? (
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={editedContact.firstName}
                                    onChange={(e) => setEditedContact(prev => ({ ...prev, firstName: e.target.value }))}
                                    placeholder="First Name"
                                    className="text-lg font-bold text-notion-text tracking-tight bg-stone-50 border border-notion-border rounded px-2 py-1 w-1/2 outline-none focus:border-stone-400"
                                />
                                <input
                                    type="text"
                                    value={editedContact.lastName}
                                    onChange={(e) => setEditedContact(prev => ({ ...prev, lastName: e.target.value }))}
                                    placeholder="Last Name"
                                    className="text-lg font-bold text-notion-text tracking-tight bg-stone-50 border border-notion-border rounded px-2 py-1 w-1/2 outline-none focus:border-stone-400"
                                />
                            </div>
                        ) : (
                            <h2 className="text-2xl font-bold text-notion-text tracking-tight">{lead.fullName || lead.name}</h2>
                        )}
                    </div>
                    <button onClick={onClose} className="p-2 bg-stone-100 rounded-full text-stone-500 ml-2">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Status Dropdown - Functional */}
                <div className="relative mb-4">
                    <select
                        value={lead.status || 'Active'}
                        onChange={handleStatusChange}
                        disabled={isSavingStatus}
                        className="w-full px-3 py-2 bg-stone-50 border border-notion-border rounded-lg text-sm font-medium text-stone-700 outline-none appearance-none cursor-pointer disabled:opacity-50"
                    >
                        {VALID_STATUSES.map(status => (
                            <option key={status} value={status}>{status}</option>
                        ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-2.5 w-4 h-4 text-stone-400 pointer-events-none" />
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-3 gap-2">
                    <button className="py-2.5 bg-stone-800 text-white rounded-lg text-xs font-medium flex items-center justify-center gap-2">
                        <Phone className="w-3.5 h-3.5" /> Call
                    </button>
                    <button
                        onClick={() => handleOpenConversation('sms')}
                        disabled={navigatingToInbox === 'sms'}
                        className="py-2.5 border border-notion-border rounded-lg text-stone-600 text-xs font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {navigatingToInbox === 'sms' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />} SMS
                    </button>
                    <button
                        onClick={() => handleOpenConversation('email')}
                        disabled={navigatingToInbox === 'email'}
                        className="py-2.5 border border-notion-border rounded-lg text-stone-600 text-xs font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {navigatingToInbox === 'email' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} Email
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-5 space-y-6 pb-20">
                {/* Contact Details - Editable */}
                <div>
                    <div className="flex justify-between items-center mb-3">
                        <h3 className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Contact</h3>
                        {!isEditingContact && (
                            <button
                                onClick={() => setIsEditingContact(true)}
                                className="text-xs text-stone-500 hover:text-stone-700 transition flex items-center gap-1"
                            >
                                <Pencil className="w-3 h-3" /> Edit
                            </button>
                        )}
                    </div>

                    {isEditingContact ? (
                        <div className="space-y-3 bg-white rounded-xl border border-notion-border p-4">
                            <div>
                                <label className="text-[10px] text-stone-400 uppercase">Email</label>
                                <input
                                    type="email"
                                    value={editedContact.email}
                                    onChange={(e) => setEditedContact(prev => ({ ...prev, email: e.target.value }))}
                                    className="w-full px-3 py-2 bg-stone-50 border border-notion-border rounded text-sm outline-none focus:border-stone-400 transition"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] text-stone-400 uppercase">Phone</label>
                                <input
                                    type="tel"
                                    value={editedContact.phone}
                                    onChange={(e) => setEditedContact(prev => ({ ...prev, phone: e.target.value }))}
                                    className="w-full px-3 py-2 bg-stone-50 border border-notion-border rounded text-sm outline-none focus:border-stone-400 transition"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] text-stone-400 uppercase">Address</label>
                                <input
                                    type="text"
                                    value={editedContact.address}
                                    onChange={(e) => setEditedContact(prev => ({ ...prev, address: e.target.value }))}
                                    className="w-full px-3 py-2 bg-stone-50 border border-notion-border rounded text-sm outline-none focus:border-stone-400 transition"
                                />
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={handleSaveContact}
                                    disabled={isSavingContact}
                                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-stone-800 text-white text-xs font-medium rounded-lg hover:bg-stone-700 disabled:opacity-50 transition"
                                >
                                    <Check className="w-3 h-3" /> Save
                                </button>
                                <button
                                    onClick={handleCancelEditContact}
                                    className="flex-1 px-3 py-2 bg-stone-100 text-stone-600 text-xs font-medium rounded-lg hover:bg-stone-200 transition"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-3 bg-white rounded-xl border border-notion-border p-4">
                            <div className="flex items-center gap-3 text-sm">
                                <Mail className="w-4 h-4 text-stone-400" />
                                <span className="text-stone-700 truncate">{lead.email || 'No email'}</span>
                            </div>
                            <div className="flex items-center gap-3 text-sm pt-3 border-t border-notion-border">
                                <Phone className="w-4 h-4 text-stone-400" />
                                <span className="text-stone-700">{lead.phone || 'No phone'}</span>
                            </div>
                            {lead.address && (
                                <div className="flex items-center gap-3 text-sm pt-3 border-t border-notion-border">
                                    <Home className="w-4 h-4 text-stone-400" />
                                    <span className="text-stone-700">{lead.address}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Tags - Editable */}
                <div>
                    <h3 className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-2">Tags</h3>
                    <TagsInput
                        tags={lead.tags || []}
                        onChange={handleTagsChange}
                        suggestions={availableTags}
                        placeholder="Add tag and press Enter..."
                        saving={isSavingTags}
                        size="md"
                    />
                </div>

                {/* Notes - Editable */}
                <div>
                    <div className="flex justify-between items-center mb-3">
                        <h3 className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Notes</h3>
                        {!isEditingNotes && (
                            <button
                                onClick={() => {
                                    setEditedNotes(lead.notes || '');
                                    setIsEditingNotes(true);
                                }}
                                className="text-xs text-stone-500 hover:text-stone-700 transition flex items-center gap-1"
                            >
                                <Pencil className="w-3 h-3" /> {lead.notes ? 'Edit' : 'Add'}
                            </button>
                        )}
                    </div>

                    {isEditingNotes ? (
                        <div className="space-y-2">
                            <textarea
                                value={editedNotes}
                                onChange={(e) => setEditedNotes(e.target.value)}
                                placeholder="Add notes about this lead... (press Enter for new entry)"
                                rows={4}
                                className="w-full px-3 py-2 bg-stone-50 border border-notion-border rounded text-sm outline-none focus:border-stone-400 transition resize-none"
                            />
                            <div className="flex gap-2">
                                <button
                                    onClick={handleSaveNotes}
                                    disabled={isSavingNotes}
                                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-stone-800 text-white text-xs font-medium rounded-lg hover:bg-stone-700 disabled:opacity-50 transition"
                                >
                                    <Check className="w-3 h-3" /> Save
                                </button>
                                <button
                                    onClick={handleCancelEditNotes}
                                    className="flex-1 px-3 py-2 bg-stone-100 text-stone-600 text-xs font-medium rounded-lg hover:bg-stone-200 transition"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-stone-50 rounded-xl p-4 min-h-[60px]">
                            {lead.notes ? (
                                <div className="space-y-1.5">
                                    {lead.notes.split('\n').filter(line => line.trim()).map((line, i) => (
                                        <div key={i} className="flex items-start gap-2">
                                            <span className="mt-1.5 w-1.5 h-1.5 bg-stone-400 rounded-full flex-shrink-0" />
                                            <p className="text-sm text-stone-600">{line}</p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-stone-400 italic">No notes yet</p>
                            )}
                        </div>
                    )}
                </div>

                {/* Interested Properties (supports multiple) */}
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Interested Properties</h3>
                        {!isEditingProperty && (
                            <button
                                onClick={() => {
                                    const propIds = (lead.interestedProperties || []).map(p => p.id);
                                    setEditedPropertyIds(propIds);
                                    setIsEditingProperty(true);
                                }}
                                className="text-xs text-blue-500 hover:text-blue-600 font-medium"
                            >
                                {(lead.interestedProperties || []).length > 0 ? 'Edit' : 'Add'}
                            </button>
                        )}
                    </div>

                    {isEditingProperty ? (
                        <div className="space-y-3">
                            <div className="max-h-48 overflow-y-auto border border-stone-200 rounded-xl bg-stone-50 divide-y divide-stone-100">
                                {propertiesLoading ? (
                                    <div className="p-3 text-sm text-stone-400">Loading properties...</div>
                                ) : properties.length === 0 ? (
                                    <div className="p-3 text-sm text-stone-400 italic">No properties available</div>
                                ) : (
                                    properties.map((property) => (
                                        <label
                                            key={property.id}
                                            className="flex items-center gap-3 px-3 py-3 hover:bg-white cursor-pointer transition"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={editedPropertyIds.includes(property.id)}
                                                onChange={() => handleToggleProperty(property.id)}
                                                className="w-5 h-5 rounded border-stone-300 text-blue-500 focus:ring-blue-500"
                                            />
                                            <span className="text-sm text-stone-700">{property.displayName}</span>
                                        </label>
                                    ))
                                )}
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={handleSaveProperty}
                                    disabled={isSavingProperty}
                                    className="flex-1 px-3 py-2 bg-blue-500 text-white text-xs font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center justify-center gap-1"
                                >
                                    {isSavingProperty ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                        <Check className="w-3 h-3" />
                                    )}
                                    Save
                                </button>
                                <button
                                    onClick={handleCancelEditProperty}
                                    disabled={isSavingProperty}
                                    className="flex-1 px-3 py-2 bg-stone-100 text-stone-700 text-xs font-medium rounded-lg hover:bg-stone-200 disabled:opacity-50 flex items-center justify-center gap-1"
                                >
                                    <X className="w-3 h-3" />
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (lead.interestedProperties || []).length > 0 ? (
                        <div className="space-y-2">
                            {lead.interestedProperties.map((prop, index) => (
                                <div key={prop.id || index} className="flex items-center gap-3 p-3 bg-white border border-notion-border rounded-xl shadow-sm">
                                    <div className="w-12 h-12 bg-stone-100 rounded-lg flex items-center justify-center text-stone-400">
                                        <Home className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-semibold text-stone-700">
                                            {prop.address || 'Property'}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-stone-500 italic">No properties selected</p>
                    )}
                </div>

                {/* Automation Progress */}
                {lead.automation && (
                    <div>
                        <h3 className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-3">Automation</h3>
                        <div className="flex items-center gap-3 p-3 bg-orange-50/50 border border-orange-100 rounded-xl">
                            <div className="w-10 h-10 bg-white rounded-lg shadow-sm flex items-center justify-center text-orange-500">
                                <Zap className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                    <span className="text-xs font-bold text-stone-700">{lead.automation.name}</span>
                                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                                </div>
                                <div className="text-[10px] text-stone-500">
                                    Step {lead.automation.step}/{lead.automation.total} • SMS tomorrow
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Actions - Archive/Delete */}
                <div className="border-t border-notion-border pt-6">
                    <h3 className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-3">Actions</h3>
                    <div className="flex gap-2">
                        <button
                            onClick={handleArchive}
                            className="flex-1 py-3 border border-notion-border rounded-xl text-stone-600 text-sm font-medium hover:bg-stone-50 transition flex items-center justify-center gap-2"
                        >
                            {lead.archived ? (
                                <>
                                    <ArchiveRestore className="w-4 h-4" /> Unarchive
                                </>
                            ) : (
                                <>
                                    <Archive className="w-4 h-4" /> Archive
                                </>
                            )}
                        </button>
                        <button
                            onClick={() => setShowDeleteConfirm(true)}
                            className="flex-1 py-3 border border-red-200 rounded-xl text-red-600 text-sm font-medium hover:bg-red-50 transition flex items-center justify-center gap-2"
                        >
                            <Trash2 className="w-4 h-4" /> Delete
                        </button>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="absolute inset-0 bg-stone-900/30 flex items-end justify-center p-4 rounded-t-2xl">
                    <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-sm">
                        <h3 className="text-lg font-semibold text-stone-800 mb-2">Delete Lead?</h3>
                        <p className="text-sm text-stone-600 mb-4">
                            This will permanently delete <strong>{lead.fullName || lead.name}</strong>. This action cannot be undone.
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                className="flex-1 py-3 border border-notion-border rounded-xl text-stone-600 text-sm font-medium hover:bg-stone-50 transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={isDeleting}
                                className="flex-1 py-3 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition"
                            >
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MobileLeadDrawer;
